import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../lib/react-query'
import { runsApi, scansApi } from '../../services/api'
import { useAuthStore } from '../../store/authStore'

const TERMINAL_STATUSES = [
  'COMPLETED', 'FAILED', 'STOPPED', 'STOP', 'PARTIAL_FAILURE', 'CANCELLED', 'CANCELED',
]

export const isTerminalScanStatus = (status?: string | null): boolean =>
  !!status && TERMINAL_STATUSES.includes(status.toUpperCase())

/** Orchestrator run detail (graph_snapshot + node_executions). Polls every 5s while the scan is non-terminal. */
export function useRunDetail(
  scanId: string | null,
  runId: string | null,
  scanStatus?: string | null,
) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.runs.detail(orgId, scanId || '', runId || ''),
    queryFn: ({ signal }) => runsApi.getById(runId!, signal),
    enabled: !!runId && !!orgId,
    refetchInterval: isTerminalScanStatus(scanStatus) ? false : 5000,
  })
}

/**
 * Same run detail, never polled — for readers that only want `graph_snapshot`.
 *
 * A run's snapshot is written once when the run is created and never mutated,
 * so re-fetching it can only return the same bytes. Shares `useRunDetail`'s
 * query key, so a column already loaded by the scan-detail page is free here.
 */
export function useRunGraph(scanId: string | null, runId: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.runs.detail(orgId, scanId || '', runId || ''),
    queryFn: ({ signal }) => runsApi.getById(runId!, signal),
    enabled: !!runId && !!orgId,
    staleTime: Infinity,
  })
}

/** Discoveries for one orchestrator node within a scan. */
export function useScanNodeResult(scanId: string, nodeId: string) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.scans.nodeResult(orgId, scanId, nodeId),
    queryFn: () => scansApi.getNodeResult(scanId, nodeId),
    enabled: !!scanId && !!nodeId && !!orgId,
  })
}
